import { useRef, useState, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import type { CustomerData, Product } from '../../types';
import { isRebateParentCategory } from '../../../../../../../utils/vendorCategoryUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customer: CustomerData;
  customerId: string;
}

const toNumber = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export default function ContractModal({ isOpen, onClose, customer, customerId }: Props) {
  const contractRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [categoryNameMap, setCategoryNameMap] = useState<Map<string, string>>(new Map());

  // 카테고리 트리 로드
  useEffect(() => {
    if (!isOpen) return;
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch('https://api.wedmin.biz/vendor/category', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await res.json();
        const tree = Array.isArray(result) ? result : result?.status ? result.data : [];
        const nameMap = new Map<string, string>();
        for (const parent of tree) {
          const pName = parent.label || parent.name || '';
          nameMap.set(pName, pName);
          for (const child of parent.children || []) {
            const cName = child.label || child.name;
            if (cName) nameMap.set(cName, pName);
          }
        }
        setCategoryNameMap(nameMap);
      } catch (e) { console.error(e); }
    };
    fetchCategories();
  }, [isOpen]);

  // 상품 목록 로드
  useEffect(() => {
    if (!isOpen || !customerId) return;
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`https://api.wedmin.biz/customer/${customerId}/detail?tab=PRODUCTS`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await res.json();
        if (result.status && result.data && Array.isArray(result.data.tabData)) {
          setProducts(result.data.tabData);
        }
      } catch (e) { console.error(e); }
    };
    fetchProducts();
  }, [isOpen, customerId]);

  const getParentCategory = useCallback((prod: any): string => {
    const vendorCatName = prod.vendor?.category?.parent?.name || prod.vendor?.category?.name || '';
    if (vendorCatName) {
      const parent = categoryNameMap.get(vendorCatName);
      if (parent) return parent;
    }
    return '기타';
  }, [categoryNameMap]);

  // 분류
  const generalItems = products.filter((p) => {
    const pc = getParentCategory(p);
    return !isRebateParentCategory(pc);
  });
  const rebateItems = products.filter((p) => {
    const pc = getParentCategory(p);
    return isRebateParentCategory(pc);
  });

  const generalTotal = generalItems.reduce((s, p) => s + toNumber(p.salesPrice) * toNumber(p.quantity || 1), 0);
  const generalCostTotal = generalItems.reduce((s, p) => s + toNumber(p.costPrice) * toNumber(p.quantity || 1), 0);
  const rebateDepositTotal = rebateItems.reduce((s, p) => s + toNumber(p.deposit), 0);
  const rebateAmountTotal = rebateItems.reduce((s, p) => s + toNumber(p.rebateAmount), 0);

  const handleDownloadPDF = async () => {
    if (!contractRef.current) return;
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(contractRef.current, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      const fileName = `계약서_${customer.groomName || ''}${customer.brideName || ''}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (e) {
      console.error('PDF 생성 실패:', e);
      alert('PDF 생성에 실패했습니다.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = async () => {
    if (!contractRef.current) return;
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(contractRef.current, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      printWindow.document.write(`
        <html><head><title>계약서</title>
        <style>@page{margin:0}body{margin:0;display:flex;justify-content:center}img{width:100%;height:auto}</style>
        </head><body><img src="${imgData}" onload="window.print();window.close()" /></body></html>
      `);
      printWindow.document.close();
    } catch (e) {
      console.error('인쇄 실패:', e);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">고객 계약서</h2>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
              <i className="ri-printer-line"></i> 인쇄
            </button>
            <button onClick={handleDownloadPDF} disabled={isGenerating}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1.5 disabled:opacity-50">
              <i className={isGenerating ? 'ri-loader-4-line animate-spin' : 'ri-download-line'}></i>
              {isGenerating ? '생성 중...' : 'PDF 다운로드'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2">
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Contract Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <div ref={contractRef} className="bg-white p-8" style={{ fontFamily: 'sans-serif' }}>
            {/* 타이틀 */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 tracking-wider">웨 딩 계 약 서</h1>
              <div className="w-16 h-0.5 bg-gray-900 mx-auto mt-3"></div>
            </div>

            {/* 고객 정보 */}
            <div className="mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-3 pb-1 border-b-2 border-gray-800">고객 정보</h2>
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-2 text-gray-500 w-24">신랑</td>
                    <td className="py-2 font-medium">{customer.groomName || '-'}</td>
                    <td className="py-2 text-gray-500 w-24">연락처</td>
                    <td className="py-2">{customer.groomContact || '-'}</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-gray-500">신부</td>
                    <td className="py-2 font-medium">{customer.brideName || '-'}</td>
                    <td className="py-2 text-gray-500">연락처</td>
                    <td className="py-2">{customer.brideContact || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 계약 정보 */}
            <div className="mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-3 pb-1 border-b-2 border-gray-800">계약 정보</h2>
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-2 text-gray-500 w-24">계약일</td>
                    <td className="py-2">{customer.contractDate || '-'}</td>
                    <td className="py-2 text-gray-500 w-24">계약유형</td>
                    <td className="py-2">{customer.contractType || '-'}</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-gray-500">총 계약금액</td>
                    <td className="py-2 font-bold text-blue-600" colSpan={3}>
                      {Number(customer.totalContractAmount || 0).toLocaleString()}원
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 예식 정보 */}
            <div className="mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-3 pb-1 border-b-2 border-gray-800">예식 정보</h2>
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-2 text-gray-500 w-24">본식일</td>
                    <td className="py-2">{customer.weddingDateTime || '-'}</td>
                    <td className="py-2 text-gray-500 w-24">본식장소</td>
                    <td className="py-2">{customer.weddingLocation || '-'}</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-gray-500">리허설일</td>
                    <td className="py-2">{customer.rehearsalDateTime || '-'}</td>
                    <td className="py-2 text-gray-500">리허설장소</td>
                    <td className="py-2">{customer.rehearsalLocation || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 일반 계약 상품 */}
            {generalItems.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-800 mb-3 pb-1 border-b-2 border-gray-800">계약 상품</h2>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-3 py-2 text-left">업체명</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">상품명</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">진행구분</th>
                      <th className="border border-gray-300 px-3 py-2 text-right">수량</th>
                      <th className="border border-gray-300 px-3 py-2 text-right">단가</th>
                      <th className="border border-gray-300 px-3 py-2 text-right">합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generalItems.map((p, i) => (
                      <tr key={i}>
                        <td className="border border-gray-300 px-3 py-2">{p.vendor?.name || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{p.productName || p.product?.name || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{p.categoryName || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right">{toNumber(p.quantity || 1)}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right">{toNumber(p.salesPrice).toLocaleString()}원</td>
                        <td className="border border-gray-300 px-3 py-2 text-right font-medium">{(toNumber(p.salesPrice) * toNumber(p.quantity || 1)).toLocaleString()}원</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={5} className="border border-gray-300 px-3 py-2 text-right">소계</td>
                      <td className="border border-gray-300 px-3 py-2 text-right">{generalTotal.toLocaleString()}원</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* 혼수 / 웨딩홀 / 허니문 계약 (리베이트 정보 제외 - 내부 수익) */}
            {rebateItems.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-800 mb-3 pb-1 border-b-2 border-gray-800">혼수 / 웨딩홀 / 허니문 계약</h2>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-3 py-2 text-left">업체명</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">계약품목</th>
                      <th className="border border-gray-300 px-3 py-2 text-right">계약금</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rebateItems.map((p, i) => (
                      <tr key={i}>
                        <td className="border border-gray-300 px-3 py-2">{p.vendor?.name || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{p.productName || p.contractItems || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right">{toNumber(p.deposit).toLocaleString()}원</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={2} className="border border-gray-300 px-3 py-2 text-right">소계</td>
                      <td className="border border-gray-300 px-3 py-2 text-right">{rebateDepositTotal.toLocaleString()}원</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* 특이사항 */}
            {customer.specialNote && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-800 mb-3 pb-1 border-b-2 border-gray-800">특이사항</h2>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{customer.specialNote}</p>
              </div>
            )}

            {/* 서명란 */}
            <div className="mt-12 pt-8 border-t border-gray-300">
              <div className="text-center text-sm text-gray-500 mb-8">
                위 내용으로 웨딩 계약을 체결합니다.
              </div>
              <div className="text-center text-sm text-gray-500 mb-8">
                {new Date().getFullYear()}년 {new Date().getMonth() + 1}월 {new Date().getDate()}일
              </div>
              <div className="grid grid-cols-2 gap-12 mt-8">
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-6">고객 (신랑/신부)</p>
                  <div className="border-b border-gray-400 mx-8 mb-2"></div>
                  <p className="text-sm text-gray-700">{customer.groomName || '_____'} / {customer.brideName || '_____'}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-6">담당 플래너</p>
                  <div className="border-b border-gray-400 mx-8 mb-2"></div>
                  <p className="text-sm text-gray-700">{customer.assignedStaffName || '_____'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">닫기</button>
        </div>
      </div>
    </div>
  );
}
